import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { ArrowLeft, Mail, Trash2, UserPlus } from 'lucide-react';
import type { PermissionSet } from '@fint/validation';
import { api, ApiError } from '@/lib/api';
import { cn } from '@/lib/cn';
import { useActiveOrg } from '@/stores/auth';
import { usePermissions } from '@/hooks/usePermissions';
import { formatDateTime } from '@/lib/dates';
import { PageHeader } from '@/app/DashboardLayout';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  EmptyState,
  ErrorState,
  Input,
  SkeletonTable,
  Table,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from '@/features/common/ui';

interface MemberRow {
  id: string;
  userId: string;
  name: string;
  email: string;
  role: 'admin' | 'member';
  roleId: string | null;
  roleName: string | null;
  status: 'active' | 'invited' | 'disabled';
  lastLoginAt: string | null;
  createdAt: string;
  isSelf: boolean;
}

interface RoleRow {
  id: string;
  name: string;
  description: string | null;
  systemKey: string | null;
  permissions: PermissionSet;
  memberCount: number;
  editable: boolean;
}

/**
 * Who is in this book, and what they can do.
 *
 * ── WHY THIS SCREEN DID NOT EXIST ──
 * The permission model was complete and unreachable: twenty-six resources with
 * scopes and actions, a roles table holding a full permission set, resolved on
 * every request with no cache - and no way to add a second person. Every
 * book had exactly its founder, as an administrator, permanently.
 *
 * ── THE ONE THING THIS SCREEN MUST NOT DO ──
 * Offer a control the server will refuse. The API returns `canManage` alongside
 * the list precisely so this page can grey out rather than guess, and every
 * refusal it can predict - your own membership, the last administrator, a role
 * that grants more than you hold - is explained here rather than surfacing as a
 * bare 400 after the click.
 */
export function MembersPage() {
  const org = useActiveOrg();
  const perms = usePermissions();
  const queryClient = useQueryClient();

  const [email, setEmail] = useState('');
  const [roleId, setRoleId] = useState('');
  const [invited, setInvited] = useState<{ email: string; otp?: string; link?: string; emailSent: boolean } | null>(
    null,
  );

  const canView = perms.can('member', 'view');

  const members = useQuery({
    queryKey: ['members', org?.id],
    queryFn: () => api.get<{ items: MemberRow[]; canManage: boolean }>('/members'),
    enabled: !!org?.id && canView,
  });

  const roles = useQuery({
    queryKey: ['roles', org?.id],
    queryFn: () => api.get<{ items: RoleRow[]; canManage: boolean }>('/roles'),
    enabled: !!org?.id && canView,
  });
  const auditorRole = roles.data?.items.find((role) => role.systemKey === 'auditor') ?? null;

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['members', org?.id] });
    void queryClient.invalidateQueries({ queryKey: ['roles', org?.id] });
  };

  const invite = useMutation({
    mutationFn: () =>
      api.post<{ email: string; otp?: string; link?: string; emailSent: boolean }>('/members', {
        email: email.trim().toLowerCase(),
        roleId,
      }),
    onSuccess: (created) => {
      setInvited(created);
      setEmail('');
      refresh();
    },
  });

  const changeRole = useMutation({
    mutationFn: (input: { id: string; roleId: string }) =>
      api.patch(`/members/${input.id}`, { roleId: input.roleId }),
    onSuccess: refresh,
  });

  const setStatus = useMutation({
    mutationFn: (input: { id: string; status: 'active' | 'disabled' }) =>
      api.patch(`/members/${input.id}`, { status: input.status }),
    onSuccess: refresh,
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/members/${id}`),
    onSuccess: refresh,
  });

  if (!canView) {
    return (
      <>
        <PageHeader title="Members" subtitle={org?.name} />
        <div className="px-5 pb-10">
          <EmptyState
            title="You cannot see this book’s members"
            description="Ask an administrator if you need access."
          />
        </div>
      </>
    );
  }

  const canManage = members.data?.canManage ?? false;
  // Any failure from any of the four mutations - they are mutually exclusive
  // in practice, and one message region is less noisy than four.
  const actionError = [invite.error, changeRole.error, setStatus.error, remove.error].find(
    (e): e is ApiError => e instanceof ApiError,
  );

  return (
    <>
      <PageHeader
        title="Members"
        subtitle={org?.name}
        actions={
          <Button variant="outline" asChild>
            <Link to="/settings">
              <ArrowLeft className="mr-1.5 h-4 w-4" />
              Settings
            </Link>
          </Button>
        }
      />

      <div className="max-w-3xl space-y-4 px-4 pb-10 sm:px-5">
        {canManage ? (
          <Card>
            <CardHeader>
              <CardTitle>Invite someone</CardTitle>
              <CardDescription>
                They get an email with a link to set their password. If they already have a Fint
                account, they simply see this book added to theirs.
              </CardDescription>
              {/* The CA, the auditor, the person filing the returns: one tap
                  picks the read-only seat and says what it can see. */}
              {auditorRole ? (
                <p className="text-sm">
                  <button
                    type="button"
                    className={cn('font-medium text-primary hover:underline', roleId === auditorRole.id && 'underline')}
                    onClick={() => setRoleId(auditorRole.id)}
                    data-testid="invite-auditor"
                  >
                    Inviting your CA or an auditor?
                  </button>{' '}
                  <span className="text-muted-foreground">
                    The Auditor role sees everything - invoices, receipts, payments, bank accounts, returns, the activity
                    log - and changes nothing. It cannot connect an AI agent.
                  </span>
                </p>
              ) : null}
            </CardHeader>
            <CardContent>
              <form
                className="flex flex-wrap items-end gap-2"
                onSubmit={(e) => {
                  e.preventDefault();
                  if (roleId) invite.mutate();
                }}
              >
                <div className="min-w-[16rem] flex-1">
                  <Input
                    label="Email"
                    type="email"
                    required
                    placeholder="name@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    error={invite.error instanceof ApiError ? invite.error.fieldError('email') : undefined}
                  />
                </div>
                <label className="min-w-[10rem] flex-1 text-sm">
                  <span className="mb-1.5 block font-medium">Role</span>
                  <select
                    required
                    className="min-h-11 w-full min-w-0 rounded-md border border-input bg-background text-base sm:min-h-9 sm:text-sm px-3"
                    value={roleId}
                    onChange={(e) => setRoleId(e.target.value)}
                  >
                    <option value="">Choose a role…</option>
                    {roles.data?.items.map((role) => (
                      <option key={role.id} value={role.id}>
                        {role.name}
                      </option>
                    ))}
                  </select>
                </label>
                <Button type="submit" loading={invite.isPending}>
                  <UserPlus className="mr-1.5 h-4 w-4" />
                  Invite
                </Button>
              </form>

              {/*
                The code, shown only when there was no way to email it. A
                self-hosted install with no SMTP would otherwise be an
                book that can never onboard anyone - the code is real,
                valid for seven days, and this is the only place it appears.
              */}
              {invited ? (
                <div className="mt-4 rounded-md border border-border bg-muted/40 p-3 text-sm">
                  {invited.emailSent ? (
                    <p className="flex items-center gap-2">
                      <Mail className="h-4 w-4 shrink-0 text-muted-foreground" />
                      Invitation sent to <strong>{invited.email}</strong>.
                    </p>
                  ) : (
                    <>
                      <p className="font-medium">
                        No email server is configured, so nothing was sent.
                      </p>
                      <p className="mt-1 text-muted-foreground">
                        Send <strong>{invited.email}</strong> this link - it opens the password screen with the code
                        filled in. It is valid for seven days and works once.
                      </p>
                      {invited.link ? (
                        <div className="mt-2 flex flex-wrap items-center gap-2">
                          <code className="min-w-0 break-all rounded bg-background px-2 py-1 text-xs" data-testid="invite-link">
                            {invited.link}
                          </code>
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              void navigator.clipboard?.writeText(invited.link ?? '');
                            }}
                          >
                            Copy link
                          </Button>
                        </div>
                      ) : null}
                      <p className="mt-2 text-muted-foreground">Or the code alone, for the “Forgot password” screen:</p>
                      <p className="mt-1 font-mono text-lg tracking-widest">{invited.otp}</p>
                    </>
                  )}
                </div>
              ) : null}
            </CardContent>
          </Card>
        ) : null}

        {actionError ? (
          <p role="alert" className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {actionError.message}
          </p>
        ) : null}

        <Card>
          <CardHeader>
            <CardTitle>People</CardTitle>
          </CardHeader>
          <CardContent>
            {members.isLoading ? (
              <SkeletonTable rows={3} cols={4} />
            ) : members.error ? (
              <ErrorState
                error={members.error}
                onRetry={() => {
                  // A block body: `no-void` allows `void` as a statement but
                  // not as an arrow's returned expression.
                  void members.refetch();
                }}
              />
            ) : (
              <Table>
                <THead>
                  <TR>
                    <TH>Person</TH>
                    <TH>Role</TH>
                    <TH>Last signed in</TH>
                    <TH />
                  </TR>
                </THead>
                <TBody>
                  {members.data?.items.map((row) => (
                    <TR key={row.id}>
                      <TD>
                        <div className="font-medium">
                          {/* The name IS the email until they sign in and set
                              their own - inventing one for an invitee would be
                              worse than showing the only thing we know. */}
                          {row.name === row.email ? row.email : row.name}
                          {row.isSelf ? <span className="ml-2 text-muted-foreground">(you)</span> : null}
                        </div>
                        {row.name === row.email ? null : (
                          <div className="text-xs text-muted-foreground">{row.email}</div>
                        )}
                        {row.status === 'disabled' ? (
                          <Badge variant="warning" className="mt-1">
                            Disabled
                          </Badge>
                        ) : null}
                      </TD>
                      <TD>
                        {canManage ? (
                          <select
                            className="min-h-11 min-w-0 rounded-md border border-input bg-background sm:min-h-8 px-2 text-sm"
                            value={row.roleId ?? ''}
                            onChange={(e) => changeRole.mutate({ id: row.id, roleId: e.target.value })}
                          >
                            {roles.data?.items.map((role) => (
                              <option key={role.id} value={role.id}>
                                {role.name}
                              </option>
                            ))}
                          </select>
                        ) : (
                          (row.roleName ?? row.role)
                        )}
                      </TD>
                      <TD className="text-muted-foreground">
                        {row.lastLoginAt ? (
                          formatDateTime(row.lastLoginAt)
                        ) : (
                          <span className="italic">Never — invitation open</span>
                        )}
                      </TD>
                      <TD>
                        {canManage ? (
                          <div className="flex justify-end gap-1">
                            {/* Disabling yourself is refused by the server, and
                                the action a person actually wants there is to
                                leave - so the button is simply not offered. */}
                            {row.isSelf ? null : (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() =>
                                  setStatus.mutate({
                                    id: row.id,
                                    status: row.status === 'disabled' ? 'active' : 'disabled',
                                  })
                                }
                              >
                                {row.status === 'disabled' ? 'Enable' : 'Disable'}
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => remove.mutate(row.id)}
                              title={row.isSelf ? 'Leave this book' : 'Remove from this book'}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        ) : null}
                      </TD>
                    </TR>
                  ))}
                </TBody>
              </Table>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Roles</CardTitle>
            <CardDescription>
              Administrator and Member are built in and are rewritten on every deploy, so a new
              feature reaches them automatically. Custom roles do not — grant them explicitly.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            {roles.data?.items.map((role) => (
              <div key={role.id} className="flex items-start justify-between gap-4">
                <div>
                  <p className="font-medium">
                    {role.name}
                    {role.systemKey ? (
                      <Badge variant="neutral" className="ml-2">
                        Built in
                      </Badge>
                    ) : null}
                  </p>
                  {role.description ? (
                    <p className="text-muted-foreground">{role.description}</p>
                  ) : null}
                </div>
                <span className="shrink-0 text-muted-foreground">
                  {role.memberCount} {role.memberCount === 1 ? 'person' : 'people'}
                </span>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    </>
  );
}
